/**
 * Opt-in multi-hop ("ultimate privacy" mode) for `@cumulusvpn/core`.
 *
 * Implements the client-side nested onion of `docs/11-multihop.md`: two stacked
 * WireGuard interfaces that share the *same* client key `K`. The outer tunnel
 * (to the ENTRY gateway) carries only the inner tunnel's packets to the exit's
 * public IP (`AllowedIPs = <exitIp>/32`); the inner tunnel (to the EXIT gateway)
 * carries all real traffic (`AllowedIPs = 0.0.0.0/0, ::/0`). No gateway protocol
 * change is required — the entry merely forwards a premium peer's traffic to
 * another gateway's `:51820`, and entitlement follows the key on every gateway,
 * so one payment covers both hops.
 *
 * Multi-hop is OFF by default (the {@link RouteStyle} `'single'` selects the
 * classic single-hop path). It trades latency and throughput for the guarantee
 * that no single operator sees both who you are and where you go.
 *
 * v1 caveat (stated honestly in-app): both hops use the same key `K`, so an
 * adversary controlling *both* chosen hops could correlate via that shared key.
 * v1.5 introduces distinct keys per hop to close that gap.
 */
import { WG_PORT } from './types.js';
import { allowedIpsFor, obfsInterfaceLines } from './wgconfig.js';
import { applyTransportToEndpoint, obfsForTransport } from './transport.js';
/** MTU for the outer interface (single WireGuard header of headroom). */
const OUTER_MTU = 1420;
/** MTU for the inner interface (headroom for two stacked WireGuard headers). */
const INNER_MTU = 1340;
/**
 * Strip a trailing `:port` from an endpoint, leaving IPv6 forms untouched.
 * `"1.2.3.4:51820"` → `"1.2.3.4"`; `"1.2.3.4"` → `"1.2.3.4"`.
 */
function stripEndpointPort(endpoint) {
    const trimmed = endpoint.trim();
    // Only strip a single trailing :port (IPv4 / host); leave IPv6 alone.
    if (trimmed.includes(':') && trimmed.split(':').length === 2) {
        return trimmed.slice(0, trimmed.lastIndexOf(':'));
    }
    return trimmed;
}
/**
 * Group key identifying a gateway's subnet for the distinct-subnet diversity
 * rule: IPv4 collapses to its /24 (first three octets), IPv6 to its /48 (first
 * three hextets, best-effort without full normalization). A malformed address
 * falls back to itself, so it only ever collides with an identical string —
 * never wrongly treating two distinct gateways as co-located.
 */
function subnetGroup(ip) {
    if (ip.includes(':')) {
        return ip.split(':').slice(0, 3).join(':').toLowerCase();
    }
    const octets = ip.split('.');
    return octets.length === 4 ? octets.slice(0, 3).join('.') : ip;
}
/**
 * Deterministic ordering: least-loaded first, then country as a stable
 * tie-break, then IP so equal-load same-country gateways still order stably.
 */
function byLoadThenCountry(a, b) {
    if (a.load !== b.load) {
        return a.load - b.load;
    }
    if (a.country !== b.country) {
        return a.country < b.country ? -1 : 1;
    }
    if (a.ip !== b.ip) {
        return a.ip < b.ip ? -1 : 1;
    }
    return 0;
}
/**
 * Choose the ordered hops for a route.
 *
 * The entry is the least-loaded gateway (deterministic tie-break by load, then
 * country, then IP), optionally constrained to `opts.entryCountry`. For
 * multi-hop styles an exit is also chosen — never equal to the entry
 * (`entry.gatewayIp !== exit.gatewayIp`) — with the jurisdiction rule for the
 * style: `'multihop-same-country'` requires `entry.country === exit.country`,
 * `'multihop-cross-jurisdiction'` requires `entry.country !== exit.country`.
 * `'single'` returns just the entry (no exit).
 *
 * With `opts.requireDistinctSubnet`, entry and exit must also fall in different
 * subnets. To honor that without spuriously failing, the least-loaded entry is
 * no longer fixed: entry candidates are tried in load order until one yields a
 * valid exit. Without the flag, behavior is unchanged — the least-loaded entry
 * is used and the call throws if it has no valid exit.
 *
 * @param gateways - Discovered, verified gateways to choose from.
 * @param style - The desired {@link RouteStyle}.
 * @param opts - Optional explicit entry/exit country picks and diversity rule.
 * @returns The chosen `entry` and, for multi-hop styles, `exit`.
 * @throws {Error} If no gateway satisfies the constraints (empty fleet, no
 *   distinct exit, no exit in the required jurisdiction, or — with
 *   `requireDistinctSubnet` — no entry/exit pair on distinct subnets).
 */
export function selectHops(gateways, style, opts = {}) {
    const sorted = [...gateways].sort(byLoadThenCountry);
    const entryPool = opts.entryCountry
        ? sorted.filter((g) => g.country === opts.entryCountry)
        : sorted;
    const entry = entryPool[0];
    if (!entry) {
        throw new Error(opts.entryCountry
            ? `selectHops: no gateway available in entry country "${opts.entryCountry}"`
            : 'selectHops: no gateways available');
    }
    if (style === 'single') {
        return { entry };
    }
    // The least-loaded valid exit for a given entry, or undefined if none.
    const exitFor = (from) => sorted.find((g) => {
        if (g.ip === from.ip) {
            return false; // enforce entry !== exit
        }
        if (opts.exitCountry && g.country !== opts.exitCountry) {
            return false;
        }
        if (opts.requireDistinctSubnet && subnetGroup(g.ip) === subnetGroup(from.ip)) {
            return false; // co-location guard: entry and exit must differ by subnet
        }
        if (style === 'multihop-same-country') {
            return g.country === from.country;
        }
        // multihop-cross-jurisdiction
        return g.country !== from.country;
    });
    // Default: fixed least-loaded entry (behavior unchanged). With the distinct-
    // subnet rule, walk entry candidates in load order so a satisfiable pair
    // isn't missed just because the least-loaded node's subnet has no partner.
    const entryCandidates = opts.requireDistinctSubnet ? entryPool : [entry];
    for (const candidate of entryCandidates) {
        const exit = exitFor(candidate);
        if (exit) {
            return { entry: candidate, exit };
        }
    }
    const jurisdiction = style === 'multihop-cross-jurisdiction'
        ? `in a different country from entry "${entry.country}"`
        : `in country "${entry.country}"`;
    const subnetNote = opts.requireDistinctSubnet ? ' on a distinct subnet' : '';
    throw new Error(`selectHops: no distinct exit ${jurisdiction}${subnetNote}`);
}
/**
 * Build the two nested WireGuard interface configs for a multi-hop route.
 *
 * Both interfaces use the *same* client private key (one payment, key `K` is
 * premium at entry and exit). The outer interface pins `AllowedIPs` to the
 * exit's IP `/32` only, so exclusively the inner tunnel's UDP packets to the
 * exit traverse the entry; the inner interface routes all traffic and sets the
 * exit's DNS and the reduced 1340 MTU.
 *
 * Stealth multi-hop obfuscates the ENTRY hop only (the local censor sees just the
 * entry handshake; the inner exit hop travels encapsulated). Pass the entry
 * gateway's chosen `entryTransport` (an `awg` transport) and the outer config
 * gains its AmneziaWG `[Interface]` lines and points at the awg port; the inner
 * (exit) hop stays vanilla. Omit it (or pass a vanilla `wg` transport) for a
 * fully vanilla multi-hop — the output is then byte-identical to before.
 *
 * Split tunneling (docs/17 §7.2): rules apply to the INNER (exit) hop — that is
 * where the default route lives. Pass a `split` compiled with
 * `supportsExcludeRoute: false` and the inner `.conf`'s `AllowedIPs` becomes the
 * compiled `tunnelRoutes`; the outer hop's `<exitIp>/32` pin is untouched. The
 * compile step must have been given both hop IPs as `endpointIps` so no rule
 * can shadow either pin. Absent or noop keeps the output byte-identical.
 *
 * @param args.privateKey - Client WireGuard private key (base64), used on both.
 * @param args.entry - Enrollment of key `K` at the ENTRY gateway.
 * @param args.exit - Enrollment of key `K` at the EXIT gateway.
 * @param args.entryTransport - ENTRY hop transport (an `awg` profile obfuscates it).
 * @param args.split - Compiled split policy for the exit hop's `AllowedIPs`.
 * @returns The `outer`/`inner` `.conf` strings, `entryEndpoint`, `exitEndpoint`, `innerMtu`.
 */
export function buildMultihopConfig(args) {
    const { privateKey, entry, exit, entryTransport, split } = args;
    const exitIp = stripEndpointPort(exit.endpoint);
    const exitEndpoint = `${exitIp}:${WG_PORT}`;
    // Stealth: fold the entry hop's awg obfuscation into the outer [Interface] and
    // point it at the transport's port (awg = 51821). Vanilla when absent.
    const entryObfs = entryTransport ? obfsForTransport(entryTransport) : undefined;
    const entryEndpoint = entryTransport
        ? applyTransportToEndpoint(entry.endpoint, entryTransport)
        : entry.endpoint;
    const outerIface = [
        '[Interface]',
        `PrivateKey = ${privateKey}`,
        `Address = ${entry.assigned_ip}/32`,
        `MTU = ${OUTER_MTU}`,
        ...obfsInterfaceLines(entryObfs),
    ];
    const outerPeer = [
        '[Peer]',
        `PublicKey = ${entry.server_pubkey}`,
        `Endpoint = ${entryEndpoint}`,
        `AllowedIPs = ${exitIp}/32`,
        'PersistentKeepalive = 25',
    ];
    const outer = `${outerIface.join('\n')}\n\n${outerPeer.join('\n')}\n`;
    // Android per-app rules (docs/17 §4.1) ride the INNER config's [Interface]
    // like the single-hop path: the official Config parser reads these keys and
    // the controller forwards them to the nested service. The compiler only
    // populates the app lists for the platform it compiled for, so a desktop
    // inner config never carries them. Empty → byte-identical output.
    const appLines = [
        ...(split && split.appsIncluded.length > 0
            ? [`IncludedApplications = ${split.appsIncluded.join(', ')}`]
            : []),
        ...(split && split.appsExcluded.length > 0
            ? [`ExcludedApplications = ${split.appsExcluded.join(', ')}`]
            : []),
    ];
    const innerIface = [
        '[Interface]',
        `PrivateKey = ${privateKey}`,
        `Address = ${exit.assigned_ip}/32`,
        `DNS = ${exit.dns}`,
        `MTU = ${INNER_MTU}`,
        ...appLines,
    ];
    const innerPeer = [
        '[Peer]',
        `PublicKey = ${exit.server_pubkey}`,
        `Endpoint = ${exit.endpoint}`,
        `AllowedIPs = ${allowedIpsFor(split)}`,
        'PersistentKeepalive = 25',
    ];
    const inner = `${innerIface.join('\n')}\n\n${innerPeer.join('\n')}\n`;
    return { outer, inner, entryEndpoint, exitEndpoint, innerMtu: INNER_MTU };
}
//# sourceMappingURL=multihop.js.map